import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, ConfigProvider, Descriptions, Empty, Input, Modal, Pagination, Result, Select, Space, Spin, Table, Typography, message, theme } from "antd";
import { CheckCircleFilled, ClockCircleFilled, InfoCircleFilled, LockOutlined, ReloadOutlined, SearchOutlined, ExportOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/es/table";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import useIsMobile from "../hooks/useIsMobile";
import useBusinessResource from "../hooks/useBusinessResource";
import BusinessResourceNotice from "../components/BusinessResourceNotice";
import { invalidateBusinessResources } from "../utils/businessResourceFreshness";
import apiClient from "../api/apiClient";
import API_CONFIG from "../config/api";
import { FeedbackItem, FeedbackStatus, feedbackError, feedbackInboxScope, feedbackScreenName, loadFeedback, loadFeedbackDetail } from "./feedbackApi";
import "./feedback.css";

const labels: Record<FeedbackStatus, string> = { new: "Nouveau", in_progress: "En cours", done: "Traité" };
const formatDate = (value: string) => dayjs(value).format("DD/MM/YYYY HH:mm");
export function FeedbackStatusBadge({ status }: { status: FeedbackStatus }) {
  const { token } = theme.useToken();
  const Icon = status === "done" ? CheckCircleFilled : status === "in_progress" ? ClockCircleFilled : InfoCircleFilled;
  const color = status === "done" ? token.green8 : status === "in_progress" ? token.gold9 : token.blue8;
  return <Space size={8} style={{ color, whiteSpace: "nowrap" }}><Icon aria-hidden="true" />{labels[status]}</Space>;
}

function FeedbackDetail({ id, onClose }: { id: number | null; onClose: () => void }) {
  const { user } = useAuth();
  const client = useQueryClient();
  const [notice, holder] = message.useMessage();
  const scope = feedbackInboxScope(user?.id);
  const contextKey = `${user?.id || ""}:${id || ""}`;
  const currentContext = useRef(contextKey); currentContext.current = contextKey;
  const detail = useBusinessResource({ resource: "feedback-detail", ...scope, parameters: [id],
    enabled: Number(user?.is_superadmin) === 1 && Boolean(id), active: Boolean(id),
    load: (signal) => loadFeedbackDetail(id!, signal) });
  const [status, setStatus] = useState<FeedbackStatus>("new");
  const [version, setVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const initialized = useRef("");
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (!id) { initialized.current = ""; setError(""); }
    else if (detail.data && detail.status === "verified" && initialized.current !== contextKey) {
      initialized.current = contextKey; setStatus(detail.data.status); setVersion(Number(detail.data.version)); setError("");
    }
  }, [id, contextKey, detail.data, detail.status]);
  const conflict = Boolean(detail.data && version && Number(detail.data.version) !== version);
  async function save() {
    if (!id || !version || inFlight.current) return;
    const capturedContext = contextKey;
    inFlight.current = true; setSaving(true); setError("");
    try {
      const latest = await detail.refresh({ force: true });
      if (!mounted.current || currentContext.current !== capturedContext) return;
      if (Number(latest.version) !== version) {
        setError("Ce retour a été modifié par une autre session. Rechargez son statut avant d’enregistrer."); return;
      }
      const response = await apiClient.patch(API_CONFIG.apiUrl(`/feedback/${id}/status`),
        { status, expected_version: version });
      await invalidateBusinessResources(client, scope, "feedback");
      if (mounted.current && currentContext.current === capturedContext) {
        const saved = response.data.data as FeedbackItem;
        setVersion(Number(saved.version)); setStatus(saved.status);
        notice.success("Statut enregistré."); onClose();
      }
    } catch (failure) {
      if (mounted.current && currentContext.current === capturedContext) {
        setError(feedbackError(failure).message);
        if (feedbackError(failure).code === "FEEDBACK_STATUS_CONFLICT") void detail.refresh().catch(() => {});
      }
    } finally { inFlight.current = false; if (mounted.current) setSaving(false); }
  }

  return <>{holder}<Modal className="feedback-detail" title="Détail du retour" open={Boolean(id)} onCancel={onClose}
    width={640} closable={!saving} maskClosable={!saving} keyboard={!saving}
    footer={<Space><Button onClick={onClose} disabled={saving}>Fermer</Button>
      <Button type="primary" size="large" loading={saving} disabled={!detail.data || !version || conflict || Boolean(detail.error) || detail.loading}
        onClick={() => { void save(); }}>Enregistrer le statut</Button></Space>}>
    <BusinessResourceNotice resource={detail} label="ce retour" />
    {!detail.data ? (!detail.error && <Spin />) : <>
      <Typography.Paragraph className="feedback-detail__message">{detail.data.message}</Typography.Paragraph>
      <Descriptions column={1} size="small" items={[
        { key: "author", label: "Auteur", children: detail.data.author_name },
        { key: "company", label: "Entreprise", children: detail.data.company_name },
        ...(detail.data.warehouse_name ? [{ key: "warehouse", label: "Établissement", children: detail.data.warehouse_name }] : []),
        { key: "screen", label: "Écran concerné", children: feedbackScreenName(detail.data.screen_path) },
        { key: "date", label: "Date de création", children: formatDate(detail.data.created_at) },
      ]} />
      {(conflict || error) && <Alert type="warning" showIcon className="feedback-detail__alert"
        message={error || "Ce retour a été modifié par une autre session. Votre choix est conservé."}
        action={conflict && <Button onClick={() => {
          setStatus(detail.data!.status); setVersion(Number(detail.data!.version)); setError("");
        }}>Recharger le statut</Button>} />}
      <label htmlFor="feedback-status" className="feedback-detail__label">Statut</label>
      <Select id="feedback-status" value={status} size="large" disabled={saving} style={{ width: "100%" }}
        onChange={setStatus} options={(Object.keys(labels) as FeedbackStatus[]).map((value) =>
          ({ value, label: labels[value] }))} />
    </>}
  </Modal></>;
}

export default function FeedbackInbox() {
  const { user } = useAuth();
  const { token } = theme.useToken();
  const isMobile = useIsMobile();
  const isSysAdmin = Number(user?.is_superadmin) === 1;
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  useEffect(() => { const timer = window.setTimeout(() => setDebouncedSearch(search), 300); return () => window.clearTimeout(timer); }, [search]);
  useEffect(() => { setSelectedId(null); }, [user?.id]);
  const resource = useBusinessResource({ resource: "feedback-inbox", ...feedbackInboxScope(user?.id),
    parameters: [page, status, debouncedSearch], enabled: isSysAdmin, active: isSysAdmin,
    load: (signal) => loadFeedback({ page, page_size: 20, status, search: debouncedSearch }, signal) });
  const readableTheme = { token: { colorTextSecondary: token.colorTextLabel,
    colorTextDescription: token.colorTextLabel, colorTextPlaceholder: token.colorTextLabel } };
  if (!isSysAdmin) return <ConfigProvider theme={readableTheme}><Result status="403" title="Accès non autorisé" subTitle="Vous ne disposez pas des droits nécessaires pour consulter cette page." /></ConfigProvider>;
  const rows = resource.data?.data || [];
  const empty = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
    description={status || debouncedSearch ? "Aucun retour ne correspond à ces filtres." : "Aucun retour reçu. Les avis des utilisateurs apparaîtront ici."} />;
  const openButton = (row: FeedbackItem) => <Button icon={<ExportOutlined />} size="large"
    aria-label={`Ouvrir le retour de ${row.author_name}`} onClick={() => setSelectedId(row.id)}>Ouvrir</Button>;
  const columns: ColumnsType<FeedbackItem> = [
    { title: "Message", dataIndex: "message_preview", key: "message", width: "34%",
      render: (value: string) => <span className="feedback-inbox__preview">{value}</span> },
    { title: "Auteur / Entreprise", key: "author", width: "20%", render: (_: unknown, row: FeedbackItem) =>
      <div className="feedback-inbox__author"><span>{row.author_name}</span><Typography.Text type="secondary">{row.company_name}</Typography.Text>
        <div className="feedback-inbox__tablet-context"><Typography.Text type="secondary">{feedbackScreenName(row.screen_path)}</Typography.Text>
          <Typography.Text type="secondary">{formatDate(row.created_at)}</Typography.Text></div></div> },
    { title: "Écran", dataIndex: "screen_path", key: "screen", responsive: ["lg"], render: feedbackScreenName },
    { title: "Date", dataIndex: "created_at", key: "date", responsive: ["lg"], render: formatDate },
    { title: "Statut", dataIndex: "status", key: "status", width: 140, render: (value: FeedbackStatus) => <FeedbackStatusBadge status={value} /> },
    { title: "", key: "open", width: 140, render: (_: unknown, row: FeedbackItem) => openButton(row) },
  ];
  return <ConfigProvider theme={readableTheme}><section className="feedback-inbox" style={{ "--feedback-surface": token.colorBgContainer,
    "--feedback-border": token.colorBorderSecondary, "--feedback-text": token.colorText,
    "--feedback-focus": token.blue8 } as React.CSSProperties}>
    <div className="feedback-inbox__heading">
      <Typography.Title level={2} style={{ color: `var(--blue-elsa, ${token.colorTextHeading})` }}>Retours utilisateurs</Typography.Title>
      <Typography.Text className="feedback-inbox__sysadmin" style={{ color: token.blue8,
        background: token.blue1, borderColor: token.blue3 }}><LockOutlined aria-hidden="true" /> SysAdmin uniquement</Typography.Text>
    </div>
    <Typography.Paragraph type="secondary">Consultez les avis reçus et suivez leur traitement.</Typography.Paragraph>
    <div className="feedback-inbox__workspace">
      <div className="feedback-inbox__filters">
        <Input size="large" prefix={<SearchOutlined />} placeholder="Rechercher un message" aria-label="Rechercher un message"
          value={search} allowClear maxLength={120} onChange={(event) => { setSearch(event.target.value); setPage(1); }} />
        <Select size="large" aria-label="Filtrer par statut" value={status} onChange={(value) => { setStatus(value); setPage(1); }}
          options={[{ value: "", label: "Tous les statuts" }, ...Object.entries(labels).map(([value, label]) => ({ value, label }))]} />
        <Button size="large" icon={<ReloadOutlined />} loading={resource.loading}
          onClick={() => { void resource.refresh({ force: true }).catch(() => {}); }}>Actualiser</Button>
      </div>
      <BusinessResourceNotice resource={resource} label="les retours utilisateurs" />
      {isMobile ? <Spin spinning={resource.loading}><div className="feedback-inbox__mobile-list">
        {!rows.length ? (!resource.error && !resource.loading && empty) : rows.map((row) =>
          <article className="feedback-inbox__mobile-row" key={row.id}>
            <FeedbackStatusBadge status={row.status} />
            <Typography.Paragraph className="feedback-inbox__preview">{row.message_preview}</Typography.Paragraph>
            <Typography.Text>{row.author_name} · {row.company_name}</Typography.Text>
            <Typography.Text type="secondary">{feedbackScreenName(row.screen_path)} · {formatDate(row.created_at)}</Typography.Text>
            {openButton(row)}
          </article>)}
      </div></Spin> : <Table<FeedbackItem> rowKey="id" columns={columns} dataSource={rows} pagination={false}
        loading={resource.loading} tableLayout="fixed" locale={{ emptyText: resource.error || resource.loading ? " " : empty }} />}
      {resource.data && <div className="feedback-inbox__footer">
        <Typography.Text type="secondary">{resource.data.total} retour{resource.data.total > 1 ? "s" : ""} au total</Typography.Text>
        <Pagination current={page} pageSize={20} total={resource.data.total} showSizeChanger={false} hideOnSinglePage={resource.data.total === 0}
          simple={isMobile} disabled={resource.loading} onChange={setPage} />
      </div>}
    </div>
    <FeedbackDetail id={selectedId} onClose={() => setSelectedId(null)} />
  </section></ConfigProvider>;
}
